import React, { useState, useEffect, useRef, useCallback } from "react";
import BillingHeader from "./BillingHeader";
import BillingFooter from "./BillingFooter";
import "./styles/BillingWindow1.css";
import Select from "react-select";

const BillingWindow = () => {
  const [masterData, setMasterData] = useState({
    invoiceNo: "1010",
    customerName: "",
    gstCode: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [rowData, setRowData] = useState([]);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({
    subtotal: 0,
    totalDiscount: 0,
    totalVat: 0,
    grandTotal: 0,
  });
  const inputRefs = useRef([]);
  const selectRefs = useRef([]); // refs for react-select controls
  const tableContainerRef = useRef(null);
  const isInitialRender = useRef(true);

  // Custom styles for react-select: increase width and reduce font size
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minWidth: 300,
      fontSize: "10px",
     FontFace: "Arial",
    }),
    menu: (provided) => ({
      ...provided,
      fontSize: "12px",
    }),
  };

  // Fetch items from API
  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await fetch("http://127.0.0.1:5000/api/items");
        const data = await response.json();
        setItems(data);
      } catch (error) {
        console.error("Error fetching items:", error);
      }
    };

    fetchItems();
  }, []);

  // Add new row with default focus on item name
  const handleAddRow = useCallback(() => {
    const newRow = {
      id: Date.now(),
      SL: "",
      itemname: "",
      itemcode: "",
      quantity: 1,
      unit: "Nos",
      pakkage: 0,
      itemprice: 0,
      discount: 0,
      vat: 0,
      vatPercentage: 0,
      total: 0,
    };

    setRowData((prevRows) => {
      const updatedRows = [newRow, ...prevRows];
      inputRefs.current = updatedRows.map(
        (_, i) => inputRefs.current[i] || React.createRef()
      );
      selectRefs.current = updatedRows.map(
        (_, i) => selectRefs.current[i] || React.createRef()
      );
      return updatedRows;
    });

    setTimeout(() => {
      if (tableContainerRef.current) {
        tableContainerRef.current.scrollTop = 0;
      }
      // Focus the item name select of the new row
      if (selectRefs.current[0]?.current) {
        selectRefs.current[0].current.focus();
      }
    }, 0);
  }, []);

  // Save the data with mandatory validation
  const handleSave = useCallback(() => {
    // Check each row for mandatory fields: itemname must be non-empty and quantity > 0
    for (let i = 0; i < rowData.length; i++) {
      const row = rowData[i];
      if (!row.itemname || row.itemname.trim() === "") {
        alert(`Row ${rowData.length - i}: Item Name is mandatory.`);
        return;
      }
      if (!row.quantity || row.quantity <= 0) {
        alert(`Row ${rowData.length - i}: Quantity must be greater than zero.`);
        return;
      }
    }
    console.log("Master Data:", masterData);
    console.log("Row Data:", rowData);
    alert("Data saved successfully!");
  }, [masterData, rowData]);

  // Calculate totals
  const updateTotal = useCallback((rows) => {
    let subtotal = 0;
    let totalDiscount = 0;
    let totalVat = 0;

    rows.forEach((row) => {
      const rowSubtotal = row.quantity * row.itemprice;
      subtotal += rowSubtotal;
      totalDiscount += rowSubtotal * (row.discount / 100);
      totalVat += row.vat;
    });

    const grandTotal = subtotal - totalDiscount + totalVat;

    setTotals({
      subtotal,
      totalDiscount,
      totalVat,
      grandTotal,
    });
  }, []);

  // Handle item selection using react-select
  const handleItemSelection = (index, selectedOption) => {
    if (!selectedOption) return;
    const selectedItem = items.find(
      (item) => item.itemname === selectedOption.value
    );

    if (selectedItem) {
      setRowData((prevRows) => {
        const updatedRows = [...prevRows];
        const row = updatedRows[index];
        const quantity = row.quantity;
        const discount = row.discount;
        const itemPrice = parseFloat(selectedItem.itemprise);
        const vatPercentage = parseFloat(selectedItem.vat);
        const vat = quantity * itemPrice * vatPercentage;
        const totalPrice = quantity * itemPrice * (1 - discount / 100) + vat;

        updatedRows[index] = {
          ...row,
          itemname: selectedItem.itemname,
          itemcode: selectedItem.itemcode,
          itemprice: itemPrice,
          unit: selectedItem.unit,
          vatPercentage,
          vat,
          total: totalPrice,
        };

        updateTotal(updatedRows);
        return updatedRows;
      });
    }
  };

  // Handle numeric input changes (e.g., quantity, discount)
  const handleValueChange = (index, field, value) => {
    setRowData((prevRows) => {
      const updatedRows = [...prevRows];
      updatedRows[index][field] = value;

      const row = updatedRows[index];
      const quantity = row.quantity;
      const discount = row.discount;
      const itemPrice = row.itemprice;
      const vatPercentage = row.vatPercentage || 0;
      const vat = quantity * itemPrice * vatPercentage;
      const totalPrice = quantity * itemPrice * (1 - discount / 100) + vat;

      updatedRows[index].vat = vat;
      updatedRows[index].total = totalPrice;

      updateTotal(updatedRows);
      return updatedRows;
    });
  };

  // Handle master section changes
  const handleMasterChange = (field, value) => {
    setMasterData({ ...masterData, [field]: value });
  };

  // Handle key press for new row creation (if Enter or Tab is pressed on the first row)
  const handleKeyPress = (e, index) => {
    if ((e.key === "Enter" || e.key === "Tab") && index === 0) {
      e.preventDefault();
      handleAddRow();
      setTimeout(() => {
        if (selectRefs.current[0]?.current) {
          selectRefs.current[0].current.focus();
        }
      }, 0);
    }
  };

  // Remove a row
  const handleRemoveRow = (index) => {
    const updatedRows = rowData.filter((_, i) => i !== index);
    setRowData(updatedRows);
    updateTotal(updatedRows);
  };

  return (
    <div className="billing-window">
      <BillingHeader />

      <div className="details-section">
        <h5 className="item-details-heading">Item Details</h5>

        <div className="table-container">
          {/* Scrollable Table Section */}
          <div className="scrollable-table" ref={tableContainerRef}>
            <table className="table table-bordered table-sm">
              <thead className="thead-dark">
                <tr>
                  <th className="col-sl">Sl No</th>
                  <th className="col-item">Item Name</th>
                  <th className="col-code">Item Code</th>
                  <th className="col-qty">Quantity</th>
                  <th className="col-unit">Unit</th>
                  <th className="col-unit">pakkage</th>
                  <th className="col-price">Item Price</th>
                  <th className="col-discount">Discount (%)</th>
                  <th className="col-vat">Vat (%)</th>
                  <th className="col-total">Total</th>
                  <th className="col-action">Actions</th>
                </tr>
              </thead>

              <tbody>
                {rowData.map((row, index) => (
                  <tr key={row.id}>
                    <td className="col-sl">{rowData.length - index}</td>
                    <td className="col-item">
                      <Select
                        ref={selectRefs.current[index]}
                        options={items.map((item) => ({
                          value: item.itemname,
                          label: item.itemname,
                        }))}
                        value={
                          row.itemname
                            ? { value: row.itemname, label: row.itemname }
                            : null
                        }
                        onChange={(selectedOption) =>
                          handleItemSelection(index, selectedOption)
                        }
                        placeholder="Select Item"
                        isSearchable={true}
                        styles={customSelectStyles}
                        classNamePrefix="react-select"
                      />
                    </td>
                    <td className="col-code">{row.itemcode}</td>
                    <td className="col-qty">
                      <input
                        type="number"
                        className="form-control"
                        value={row.quantity}
                        onChange={(e) =>
                          handleValueChange(index, "quantity", +e.target.value)
                        }
                        onKeyDown={(e) => handleKeyPress(e, index)}
                        ref={inputRefs.current[index]}
                      />
                    </td>
                    <td className="col-unit">{row.unit}</td>
                    <td className="col-unit">{row.pakkage}</td>
                    <td className="col-price">{row.itemprice.toFixed(2)}</td>
                    <td className="col-discount">
                      <input
                        type="number"
                        className="form-control"
                        value={row.discount}
                        min="0"
                        max="100"
                        onChange={(e) =>
                          handleValueChange(index, "discount", +e.target.value)
                        }
                      />
                    </td>
                    <td className="col-vat">{row.vat.toFixed(2)}</td>
                    <td className="col-total">{row.total.toFixed(2)}</td>
                    <td className="col-action">
                      <button
                        className="btn btn-danger"
                        onClick={() => handleRemoveRow(index)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Fixed Footer Section */}
          <div className="fixed-footer">
            <table className="table table-bordered table-sm">
              <tfoot>
                <tr>
                  <td className="col-sl"></td>
                  <td className="col-item"></td>
                  <td className="col-code"></td>
                  <td className="col-qty"></td>
                  <td className="col-unit text-end fw-bold">Total:</td>
                  <td className="col-price fw-bold">
                    ₹{totals.subtotal.toFixed(2)}
                  </td>
                  <td className="col-discount fw-bold">
                    ₹{totals.totalDiscount.toFixed(2)}
                  </td>
                  <td className="col-vat fw-bold">
                    ₹{totals.totalVat.toFixed(2)}
                  </td>
                  <td className="col-total fw-bold">
                    ₹{totals.grandTotal.toFixed(2)}
                  </td>
                  <td className="col-action"></td>
                </tr>
                <tr>
                  <td colSpan="4" className="text-end fw-bold">
                    Grand Total:
                  </td>
                  <td colSpan="5" className="fw-bold text-center">
                    ₹{totals.grandTotal.toFixed(2)}
                  </td>
                  <td className="col-action"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <BillingFooter
        total={totals.grandTotal}
        onAddRow={handleAddRow}
        onSave={handleSave}
      />
    </div>
  );
};

export default BillingWindow;
